import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Alert, Badge, Tooltip, OverlayTrigger, InputGroup } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import { apiGet, apiPut, apiDelete } from '../utils/apiConfig';
import 'bootstrap/dist/css/bootstrap.min.css';

function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [mensaje, setMensaje] = useState('');
  const [tipoMensaje, setTipoMensaje] = useState('success');
  const [sidebarAbierto, setSidebarAbierto] = useState(true);
  const [mostrarInactivos, setMostrarInactivos] = useState(false);
  
  // Estados para filtros de búsqueda
  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroCorreo, setFiltroCorreo] = useState('');
  const [filtroRol, setFiltroRol] = useState('');
  
  // Estados para modal de edición de permisos
  const [showModal, setShowModal] = useState(false);
  const [usuarioEditando, setUsuarioEditando] = useState(null);
  const [permisosEditando, setPermisosEditando] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Estados para modal de edición de datos básicos
  const [showModalDatos, setShowModalDatos] = useState(false);
  const [datosEditando, setDatosEditando] = useState({ nombres: '', correo: '',tipoDocumento: 'CC', numeroDocumento: '' });
  const [guardandoDatos, setGuardandoDatos] = useState(false);

  // Estados para modal de firma
  const [showModalFirma, setShowModalFirma] = useState(false);
  const [usuarioFirma, setUsuarioFirma] = useState(null);
  const [firmaPreview, setFirmaPreview] = useState(null);
  const [subiendoFirma, setSubiendoFirma] = useState(false);
  const fileInputRef = useRef(null);

  // Estados para modal de ver firma
  const [showModalVerFirma, setShowModalVerFirma] = useState(false);
  const [firmaViendo, setFirmaViendo] = useState(null);

  // Estados para toast
  const [showToast, setShowToast] = useState(false);
  const [toastTipo, setToastTipo] = useState('success');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await apiGet('/api/users');
      setUsers(data);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      mostrarToast('Error al cargar usuarios', 'error');
    }
  };

  // Sistema de toast mejorado
  const mostrarToast = (texto, tipo = 'success', duracion = 3000) => {
    setMensaje(texto);
    setTipoMensaje(tipo);
    setShowToast(true);
    
    if (tipo !== 'loading') {
      setTimeout(() => {
        setShowToast(false);
      }, duracion);
    }
  };

  const ocultarToast = () => {
    setShowToast(false);
  };

  // Función para desactivar usuario
  const handleDesactivar = async (id, nombre) => {
    if (!window.confirm(`¿Desactivar el usuario ${nombre}? Podrá reactivarse después.`)) return;

    mostrarToast('Desactivando usuario...', 'loading');

    try {
      await apiDelete(`/api/delete/${id}`);
      mostrarToast(`Usuario ${nombre} desactivado correctamente`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error al desactivar usuario:', error);
      mostrarToast('Error al desactivar usuario', 'error');
    }
  };

  // Función para reactivar usuario
  const handleReactivar = async (id, nombre) => {
    if (!window.confirm(`¿Reactivar el usuario ${nombre}?`)) return;

    mostrarToast('Reactivando usuario...', 'loading');

    try {
      await apiPut(`/api/reactivate-user/${id}`);
      mostrarToast(`Usuario ${nombre} reactivado correctamente`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error al reactivar usuario:', error);
      mostrarToast('Error al reactivar usuario', 'error');
    }
  };

  // Función para eliminar permanentemente (solo superadmin)
  const handleEliminarPermanente = async (id, nombre) => {
    const confirmacion1 = window.confirm(
      `⚠️ ADVERTENCIA: Vas a eliminar PERMANENTEMENTE al usuario ${nombre}.\n\nEsta acción NO se puede deshacer.\n\n¿Estás completamente seguro?`
    );
    
    if (!confirmacion1) return;

    const confirmacion2 = window.confirm(
      `Última confirmación: ¿Eliminar permanentemente a ${nombre}?\n\nEscribe "ELIMINAR" para confirmar o cancela.`
    );

    if (!confirmacion2) return;

    mostrarToast('Eliminando usuario permanentemente...', 'loading');

    try {
      await apiDelete(`/api/delete-permanently/${id}`);
      mostrarToast(`Usuario ${nombre} eliminado permanentemente`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      mostrarToast('Error al eliminar usuario permanentemente', 'error');
    }
  };

  // Función para desbloquear usuario
  const handleDesbloquear = async (userId, nombre) => {
    if (!window.confirm(`¿Desbloquear el usuario ${nombre}?`)) return;

    mostrarToast('Desbloqueando usuario...', 'loading');

    try {
      await apiPut(`/api/desbloquear-usuario/${userId}`);
      mostrarToast(`Usuario ${nombre} desbloqueado correctamente`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error al desbloquear usuario:', error);
      mostrarToast('Error al desbloquear usuario', 'error');
    }
  };

  // ============================================
  // FUNCIONES PARA EDICIÓN DE DATOS BÁSICOS
  // ============================================

  const handleAbrirModalDatos = (usuario) => {
    setUsuarioEditando(usuario);
    setDatosEditando({
      nombres: usuario.nombres,
      correo: usuario.correo,
      tipoDocumento: usuario.tipoDocumento || 'CC',
      numeroDocumento: usuario.numeroDocumento || ''
    });
    setShowModalDatos(true);
  };

  const handleGuardarDatos = async () => {
    if (!usuarioEditando) return;

    // Validaciones
    if (!datosEditando.nombres.trim()) {
      mostrarToast('El nombre no puede estar vacío', 'error');
      return;
    }

    if (!datosEditando.correo.trim()) {
      mostrarToast('El correo no puede estar vacío', 'error');
      return;
    }

    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(datosEditando.correo)) {
      mostrarToast('El formato del correo no es válido', 'error');
      return;
    }

    // Validar número de documento
    if (!datosEditando.numeroDocumento.trim()) {
      mostrarToast('El número de documento es obligatorio', 'error');
      return;
    }

    if (datosEditando.numeroDocumento.length < 5 || datosEditando.numeroDocumento.length > 15) {
      mostrarToast('El número de documento debe tener entre 5 y 15 caracteres', 'error');
      return;
    }

    setGuardandoDatos(true);

    try {
      await apiPut(`/api/update/${usuarioEditando._id}`, {
        nombres: datosEditando.nombres.trim(),
        correo: datosEditando.correo.trim().toLowerCase(),
        tipoDocumento: datosEditando.tipoDocumento,
        numeroDocumento: datosEditando.numeroDocumento.trim()
      });

      mostrarToast(`Datos de ${datosEditando.nombres} actualizados correctamente`, 'success');
      setShowModalDatos(false);
      fetchUsers();
    } catch (error) {
      console.error('Error al actualizar datos:', error);
      mostrarToast(error.response?.data?.mensaje || 'Error al actualizar datos del usuario', 'error');
    } finally {
      setGuardandoDatos(false);
    }
  };

  // ============================================
  // FUNCIONES PARA EDICIÓN DE PERMISOS
  // ============================================

  const handleEditarPermisos = (usuario) => {
    setUsuarioEditando(usuario);
    setPermisosEditando(usuario.permisos || {});
    setShowModal(true);
  };

  const handlePermisoChange = (permiso, valor) => {
    setPermisosEditando(prev => ({
      ...prev,
      [permiso]: valor
    }));
  };

  const handleGuardarPermisos = async () => {
    if (!usuarioEditando) return;

    setGuardando(true);

    try {
      await apiPut(`/api/update/${usuarioEditando._id}`, {
        permisos: permisosEditando
      });

      mostrarToast(`Permisos de ${usuarioEditando.nombres} actualizados`, 'success');
      setShowModal(false);
      fetchUsers();
    } catch (error) {
      console.error('Error al actualizar permisos:', error);
      mostrarToast('Error al actualizar permisos', 'error');
    } finally {
      setGuardando(false);
    }
  };

  // ============================================
  // FUNCIONES PARA GESTIÓN DE FIRMAS
  // ============================================

  const handleAbrirModalFirma = (usuario) => {
    setUsuarioFirma(usuario);
    setFirmaPreview(null);
    setShowModalFirma(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      mostrarToast('Por favor selecciona un archivo de imagen', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      mostrarToast('La imagen no debe superar 2MB', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFirmaPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubirFirma = async () => {
    if (!firmaPreview) {
      mostrarToast('Por favor selecciona una imagen primero', 'error');
      return;
    }

    setSubiendoFirma(true);

    try {
      await apiPut(`/api/subir-firma/${usuarioFirma._id}`, {
        firmaBase64: firmaPreview
      });

      mostrarToast(`Firma de ${usuarioFirma.nombres} guardada correctamente`, 'success');
      setShowModalFirma(false);
      setFirmaPreview(null);
      fetchUsers();
    } catch (error) {
      console.error('Error al subir firma:', error);
      mostrarToast('Error al guardar la firma', 'error');
    } finally {
      setSubiendoFirma(false);
    }
  };

  const handleVerFirma = async (usuario) => {
  try {
    mostrarToast('Cargando firma...', 'loading');
    
    const data = await apiGet(`/api/obtener-firma/${usuario._id}`);
    
    setFirmaViendo({
      nombres: usuario.nombres,
      firma: data.firma
    });
    setShowModalVerFirma(true);
    ocultarToast();
    
  } catch (error) {
    console.error('Error al obtener firma:', error);
    mostrarToast('Error al cargar la firma', 'error');
  }
};

  const handleEliminarFirma = async (usuario) => {
    if (!window.confirm(`¿Eliminar la firma de ${usuario.nombres}?`)) return;

    mostrarToast('Eliminando firma...', 'loading');

    try {
      await apiDelete(`/api/eliminar-firma/${usuario._id}`);
      mostrarToast(`Firma de ${usuario.nombres} eliminada correctamente`, 'success');
      fetchUsers();
    } catch (error) {
      console.error('Error al eliminar firma:', error);
      mostrarToast('Error al eliminar la firma', 'error');
    }
  };

  // ============================================
  // FUNCIONES AUXILIARES
  // ============================================

  const getEstadoBadge = (usuario) => {
    if (!usuario.activo) {
      return <Badge bg="secondary">Inactivo</Badge>;
    }
    if (usuario.bloqueado) {
      return <Badge bg="danger">Bloqueado</Badge>;
    }
    if (usuario.intentosFallidos > 0) {
      return <Badge bg="warning">⚠️ {usuario.intentosFallidos} intentos</Badge>;
    }
    return <Badge bg="success">Activo</Badge>;
  };

  const getResumenPermisos = (permisos) => {
    if (!permisos) return 'Sin permisos';
    
    const permisosActivos = Object.values(permisos).filter(p => p === true).length;
    const totalPermisos = Object.keys(permisos).length;
    
    return `${permisosActivos}/${totalPermisos} permisos`;
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return 'Nunca';
    
    try {
      const date = new Date(fecha);
      if (isNaN(date.getTime())) return 'Fecha inválida';
      
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Error en fecha';
    }
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroCorreo('');
    setFiltroRol('');
  };

  // Filtrar usuarios
  const usuariosFiltrados = users.filter(user => {
    // Filtro por estado activo/inactivo
    if (!mostrarInactivos && !user.activo) return false;

    // Filtro por nombre
    if (filtroNombre && !user.nombres.toLowerCase().includes(filtroNombre.toLowerCase())) {
      return false;
    }

    // Filtro por correo
    if (filtroCorreo && !user.correo.toLowerCase().includes(filtroCorreo.toLowerCase())) {
      return false;
    }

    // Filtro por rol
    if (filtroRol && user.rol !== filtroRol) {
      return false;
    }

    return true;
  });

  // Obtener usuario actual para verificar permisos
  const usuarioActual = {
    rol: localStorage.getItem('rol')
  };

  const esSuperAdmin = usuarioActual.rol === 'superadmin';

  return (
    <>
      <style jsx>{`
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 9999;
          max-width: 350px;
        }

        .toast-notification {
          background: white;
          border-radius: 10px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          padding: 20px;
          margin-bottom: 10px;
          border-left: 5px solid;
          animation: slideInRight 0.3s ease-out;
          position: relative;
        }

        .toast-notification.success {
          border-left-color: #28a745;
        }

        .toast-notification.error {
          border-left-color: #dc3545;
        }

        .toast-notification.loading {
          border-left-color: #007bff;
        }

        .toast-header {
          display: flex;
          align-items: center;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .toast-icon {
          font-size: 20px;
          margin-right: 10px;
        }

        .toast-icon.success {
          color: #28a745;
        }

        .toast-icon.error {
          color: #dc3545;
        }

        .toast-icon.loading {
          color: #007bff;
          animation: spin 1s linear infinite;
        }

        .toast-close {
          position: absolute;
          top: 10px;
          right: 15px;
          background: none;
          border: none;
          font-size: 18px;
          cursor: pointer;
          color: #999;
        }

        .toast-message {
          color: #333;
          line-height: 1.4;
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .toggle-switch {
          position: relative;
          display: inline-block;
        }

        .toggle-input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-label {
          display: block;
          width: 50px;
          height: 24px;
          background: #ccc;
          border-radius: 34px;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
        }

        .toggle-input:checked + .toggle-label {
          background: #28a745;
          box-shadow: 0 0 10px rgba(40, 167, 69, 0.3);
        }

        .toggle-button {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 20px;
          height: 20px;
          background: white;
          border-radius: 50%;
          transition: all 0.3s ease;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .toggle-input:checked + .toggle-label .toggle-button {
          transform: translateX(24px);
        }

        .toggle-button::before {
          content: "✕";
          color: #dc3545;
          font-weight: bold;
          font-size: 12px;
          transition: all 0.3s ease;
        }

        .toggle-input:checked + .toggle-label .toggle-button::before {
          content: "✓";
          color: #28a745;
        }

        .permission-item-modal {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 10px;
          transition: all 0.3s ease;
        }

        .permission-item-modal:hover {
          background: #f1f3f4;
          border-color: #dee2e6;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .firma-preview-container {
          border: 2px dashed #dee2e6;
          border-radius: 8px;
          padding: 20px;
          text-align: center;
          background: #f8f9fa;
          min-height: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .firma-preview-image {
          max-width: 100%;
          max-height: 300px;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: white;
          padding: 10px;
        }

        .firma-thumbnail {
          width: 60px;
          height: 60px;
          object-fit: contain;
          border: 1px solid #dee2e6;
          border-radius: 4px;
          background: white;
          padding: 5px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .firma-thumbnail:hover {
          transform: scale(1.1);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
        }

        .filter-badge {
          background: #007bff;
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 12px;
          margin-left: 8px;
        }
      `}</style>

      <div className="d-flex">
        <Sidebar onToggle={setSidebarAbierto} />

        <div 
          className="flex-grow-1 p-4" 
          style={{ 
            marginLeft: sidebarAbierto ? '240px' : '80px', 
            transition: 'margin-left 0.3s',
            minHeight: '100vh',
            backgroundColor: '#f8f9fa'
          }}
        >
          <div className="container-fluid">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="mb-1">Administración de Usuarios</h2>
                <p className="text-muted mb-0">Gestión de permisos, roles y estados de usuario</p>
              </div>
              <div>
                <Badge bg="info" className="me-2">
                  {users.filter(u => u.activo).length} Activos
                </Badge>
                <Badge bg="secondary" className="me-2">
                  {users.filter(u => !u.activo).length} Inactivos
                </Badge>
                <Badge bg="warning">
                  {users.filter(u => u.bloqueado).length} Bloqueados
                </Badge>
              </div>
            </div>

            {/* FILTROS DE BÚSQUEDA */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="row g-3 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">
                      🔍 Buscar por nombre
                    </label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Nombre del usuario..."
                        value={filtroNombre}
                        onChange={(e) => setFiltroNombre(e.target.value)}
                      />
                      {filtroNombre && (
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => setFiltroNombre('')}
                          title="Limpiar"
                        >
                          ✕
                        </Button>
                      )}
                    </InputGroup>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">
                      📧 Buscar por correo
                    </label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="Correo electrónico..."
                        value={filtroCorreo}
                        onChange={(e) => setFiltroCorreo(e.target.value)}
                      />
                      {filtroCorreo && (
                        <Button 
                          variant="outline-secondary" 
                          onClick={() => setFiltroCorreo('')}
                          title="Limpiar"
                        >
                          ✕
                        </Button>
                      )}
                    </InputGroup>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label fw-bold mb-1">
                      👤 Filtrar por rol
                    </label>
                    <Form.Select
                      value={filtroRol}
                      onChange={(e) => setFiltroRol(e.target.value)}
                    >
                      <option value="">Todos los roles</option>
                      <option value="superadmin">Super Admin</option>
                      <option value="enfermeria">Enfermería</option>
                      <option value="administrativo">Administrativo</option>
                      <option value="farmacia">Farmacia</option>
                    </Form.Select>
                  </div>

                  <div className="col-md-3">
                    <Button 
                      variant="outline-secondary" 
                      className="w-100"
                      onClick={limpiarFiltros}
                    >
                      🗑️ Limpiar Filtros
                    </Button>
                  </div>
                </div>

                {/* Indicador de filtros activos */}
                <div className="mt-3">
                  <div className="d-flex align-items-center">
                    <div className="form-check form-switch me-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="toggleInactivos"
                        checked={mostrarInactivos}
                        onChange={(e) => setMostrarInactivos(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="toggleInactivos">
                        Incluir usuarios inactivos
                      </label>
                    </div>
                    
                    <span className="text-muted">
                      Mostrando <strong>{usuariosFiltrados.length}</strong> de <strong>{users.length}</strong> usuarios
                      {(filtroNombre || filtroCorreo || filtroRol) && (
                        <span className="filter-badge">Filtros activos</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* TABLA DE USUARIOS */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="card-title mb-0">Lista de Usuarios</h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Usuario</th>
                        <th>Rol</th>
                        <th>Estado</th>
                        <th>Permisos</th>
                        <th className="text-center">Firma</th>
                        <th>Último Acceso</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usuariosFiltrados.map((user) => (
                        <tr key={user._id} className={!user.activo ? 'table-secondary' : ''}>
                          <td>
                            <div>
                              <div className="fw-bold">{user.nombres}</div>
                              <small className="text-muted d-block">{user.correo}</small>
                              {user.tipoDocumento && user.numeroDocumento && (
                                <small className="text-muted">
                                  <strong>{user.tipoDocumento}:</strong> {user.numeroDocumento}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>
                            <Badge 
                              bg={user.rol === 'superadmin' ? 'primary' : 
                                  user.rol === 'enfermeria' ? 'info' : 
                                  user.rol === 'administrativo' ? 'warning' : 'secondary'}
                              className="text-capitalize"
                            >
                              {user.rol.replace('admin', ' Admin')}
                            </Badge>
                          </td>
                          <td>
                            {getEstadoBadge(user)}
                          </td>
                          <td>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip>
                                  {user.permisos ? Object.entries(user.permisos)
                                    .filter(([key, value]) => value)
                                    .map(([key]) => key)
                                    .join(', ') || 'Sin permisos'
                                    : 'Sin permisos configurados'
                                  }
                                </Tooltip>
                              }
                            >
                              <small className="text-muted" style={{cursor: 'help'}}>
                                {getResumenPermisos(user.permisos)}
                              </small>
                            </OverlayTrigger>
                          </td>
                          <td className="text-center">
  {user.tieneFirma ? (
    <div className="d-flex justify-content-center align-items-center gap-2">
      <button
        onClick={() => handleVerFirma(user)}
        className="btn btn-sm btn-outline-success"
        title="Ver firma"
      >
        ✅ Ver Firma
      </button>
      <button
        onClick={() => handleEliminarFirma(user)}
        className="btn btn-sm btn-outline-danger"
        title="Eliminar firma"
      >
        🗑️
      </button>
    </div>
  ) : (
    <button
      onClick={() => handleAbrirModalFirma(user)}
      className="btn btn-sm btn-outline-warning"
      title="Subir firma"
    >
      ⚠️ Cargar Firma
    </button>
  )}
</td>
                          <td>
                            <small className="text-muted">
                              {formatearFecha(user.ultimoAcceso)}
                            </small>
                          </td>
                          <td className="text-center">
                            <div className="btn-group" role="group">
                              {/* Botón Editar Datos Básicos */}
                              <button
                                onClick={() => handleAbrirModalDatos(user)}
                                className="btn btn-outline-success btn-sm"
                                title="Editar nombre y correo"
                              >
                                ✏️
                              </button>

                              {/* Botón Editar Permisos */}
                              <button
                                onClick={() => handleEditarPermisos(user)}
                                className="btn btn-outline-primary btn-sm"
                                title="Editar permisos"
                              >
                                ⚙️
                              </button>

                              {/* Botón Desbloquear (solo si está bloqueado) */}
                              {user.bloqueado && (
                                <button
                                  onClick={() => handleDesbloquear(user._id, user.nombres)}
                                  className="btn btn-outline-warning btn-sm"
                                  title="Desbloquear usuario"
                                >
                                  🔓
                                </button>
                              )}

                              {/* Botones según estado del usuario */}
                              {user.activo ? (
                                // Usuario activo: mostrar botón desactivar
                                <button
                                  onClick={() => handleDesactivar(user._id, user.nombres)}
                                  className="btn btn-outline-secondary btn-sm"
                                  title="Desactivar usuario (reversible)"
                                >
                                  ⏸️
                                </button>
                              ) : (
                                // Usuario inactivo: mostrar botones reactivar y eliminar
                                <>
                                  <button
                                    onClick={() => handleReactivar(user._id, user.nombres)}
                                    className="btn btn-outline-success btn-sm"
                                    title="Reactivar usuario"
                                  >
                                    ▶️
                                  </button>
                                  
                                  {/* Eliminar permanente (solo superadmin) */}
                                  {esSuperAdmin && (
                                    <button
                                      onClick={() => handleEliminarPermanente(user._id, user.nombres)}
                                      className="btn btn-outline-danger btn-sm"
                                      title="ELIMINAR PERMANENTEMENTE (irreversible)"
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {usuariosFiltrados.length === 0 && (
              <div className="text-center py-5">
                <p className="text-muted">
                  {users.length === 0 ? 
                    'No hay usuarios registrados' : 
                    'No se encontraron usuarios con los filtros aplicados'
                  }
                </p>
                {(filtroNombre || filtroCorreo || filtroRol) && (
                  <Button variant="outline-primary" onClick={limpiarFiltros}>
                    Limpiar filtros
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODAL PARA EDITAR DATOS BÁSICOS */}
      <Modal show={showModalDatos} onHide={() => setShowModalDatos(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            ✏️ Editar Usuario
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usuarioEditando && (
            <Form>
              <div className="row">
                {/* Nombre Completo */}
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Nombre Completo</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nombre del usuario"
                    value={datosEditando.nombres}
                    onChange={(e) => setDatosEditando({...datosEditando, nombres: e.target.value})}
                  />
                </div>

                {/* Correo Electrónico */}
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Correo Electrónico</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="correo@ejemplo.com"
                    value={datosEditando.correo}
                    onChange={(e) => setDatosEditando({...datosEditando, correo: e.target.value})}
                  />
                  <Form.Text className="text-muted">
                    Se convertirá a minúsculas
                  </Form.Text>
                </div>

                {/* Tipo de Documento */}
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Tipo de Documento</Form.Label>
                  <Form.Select
                    value={datosEditando.tipoDocumento}
                    onChange={(e) => setDatosEditando({...datosEditando, tipoDocumento: e.target.value})}
                  >
                    <option value="CC">Cédula de Ciudadanía</option>
                    <option value="TI">Tarjeta de Identidad</option>
                    <option value="CE">Cédula de Extranjería</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="PEP">Permiso Especial de Permanencia</option>
                  </Form.Select>
                </div>

                {/* Número de Documento */}
                <div className="col-md-6 mb-3">
                  <Form.Label className="fw-bold">Número de Documento</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ej: 1234567890"
                    value={datosEditando.numeroDocumento}
                    onChange={(e) => setDatosEditando({
                      ...datosEditando, 
                      numeroDocumento: e.target.value.replace(/[^0-9]/g, '')
                    })}
                    maxLength="15"
                  />
                  <Form.Text className="text-muted">
                    Solo números (5-15 dígitos)
                  </Form.Text>
                </div>
              </div>

              <div className="alert alert-info mb-0">
                <small>
                  <strong>Nota:</strong> El rol y los permisos se editan desde sus respectivos botones.
                </small>
              </div>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalDatos(false)}>
            Cancelar
          </Button>
          <Button 
            variant="success" 
            onClick={handleGuardarDatos}
            disabled={guardandoDatos}
          >
            {guardandoDatos ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Guardando...
              </>
            ) : (
              '💾 Guardar Cambios'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL PARA EDITAR PERMISOS */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            ⚙️ Editar Permisos - {usuarioEditando?.nombres}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {usuarioEditando && (
            <div>
              {/* Información del usuario */}
              <div className="mb-4 p-3" style={{
                background: 'linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)',
                border: '1px solid #bbdefb',
                borderRadius: '8px'
              }}>
                <div className="row text-center">
                  <div className="col-md-6">
                    <strong>Usuario:</strong> {usuarioEditando.nombres}
                  </div>
                  <div className="col-md-6">
                    <strong>Rol:</strong> <span className="text-capitalize">{usuarioEditando.rol}</span>
                  </div>
                </div>
              </div>

              <div className="row">
                {/* Permisos de Visualización */}
                <div className="col-md-6">
                  <h6 className="text-primary mb-3 text-center fw-bold">Permisos de Visualización</h6>
                  
                  <div className="permission-item-modal">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <label className="form-label mb-1 fw-bold">Ver Documentos Médicos</label>
                        <small className="d-block text-muted">Consentimientos, autorizaciones médicas</small>
                      </div>
                      <div className="ms-3">
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="verDocumentosMedicos"
                            checked={permisosEditando.verDocumentosMedicos || false}
                            onChange={() => handlePermisoChange('verDocumentosMedicos', !permisosEditando.verDocumentosMedicos)}
                            className="toggle-input"
                          />
                          <label htmlFor="verDocumentosMedicos" className="toggle-label">
                            <span className="toggle-button"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="permission-item-modal">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <label className="form-label mb-1 fw-bold">Ver Documentos Administrativos</label>
                        <small className="d-block text-muted">Contratos, información sensible</small>
                      </div>
                      <div className="ms-3">
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="verDocumentosAdministrativos"
                            checked={permisosEditando.verDocumentosAdministrativos || false}
                            onChange={() => handlePermisoChange('verDocumentosAdministrativos', !permisosEditando.verDocumentosAdministrativos)}
                            className="toggle-input"
                          />
                          <label htmlFor="verDocumentosAdministrativos" className="toggle-label">
                            <span className="toggle-button"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="permission-item-modal">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <label className="form-label mb-1 fw-bold">Ver Reportes</label>
                        <small className="d-block text-muted">Estadísticas y métricas del sistema</small>
                      </div>
                      <div className="ms-3">
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="verReportes"
                            checked={permisosEditando.verReportes || false}
                            onChange={() => handlePermisoChange('verReportes', !permisosEditando.verReportes)}
                            className="toggle-input"
                          />
                          <label htmlFor="verReportes" className="toggle-label">
                            <span className="toggle-button"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Permisos de Acción */}
                <div className="col-md-6">
                  <h6 className="text-success mb-3 text-center fw-bold">Permisos de Acción</h6>
                  
                  <div className="permission-item-modal">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <label className="form-label mb-1 fw-bold">Enviar Documentos Médicos</label>
                        <small className="d-block text-muted">Crear y enviar documentos médicos</small>
                      </div>
                      <div className="ms-3">
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="enviarDocumentosMedicos"
                            checked={permisosEditando.enviarDocumentosMedicos || false}
                            onChange={() => handlePermisoChange('enviarDocumentosMedicos', !permisosEditando.enviarDocumentosMedicos)}
                            className="toggle-input"
                          />
                          <label htmlFor="enviarDocumentosMedicos" className="toggle-label">
                            <span className="toggle-button"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="permission-item-modal">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <label className="form-label mb-1 fw-bold">Enviar Documentos Administrativos</label>
                        <small className="d-block text-muted">Crear y enviar documentos administrativos</small>
                      </div>
                      <div className="ms-3">
                        <div className="toggle-switch">
                          <input
                            type="checkbox"
                            id="enviarDocumentosAdministrativos"
                            checked={permisosEditando.enviarDocumentosAdministrativos || false}
                            onChange={() => handlePermisoChange('enviarDocumentosAdministrativos', !permisosEditando.enviarDocumentosAdministrativos)}
                            className="toggle-input"
                          />
                          <label htmlFor="enviarDocumentosAdministrativos" className="toggle-label">
                            <span className="toggle-button"></span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sección de Administración */}
                  <div className="mt-4">
                    <h6 className="text-warning mb-3 text-center fw-bold">Administración</h6>
                    
                    <div className="permission-item-modal">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="flex-grow-1">
                          <label className="form-label mb-1 fw-bold">Gestionar Usuarios</label>
                          <small className="d-block text-muted">
                            {usuarioEditando.rol === 'superadmin' ? 
                              'Crear, editar y administrar usuarios' : 
                              'Solo disponible para SuperAdmin'
                            }
                          </small>
                        </div>
                        <div className="ms-3">
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              id="gestionarUsuarios"
                              checked={permisosEditando.gestionarUsuarios || false}
                              onChange={() => handlePermisoChange('gestionarUsuarios', !permisosEditando.gestionarUsuarios)}
                              className="toggle-input"
                              disabled={usuarioEditando.rol !== 'superadmin'}
                            />
                            <label htmlFor="gestionarUsuarios" className="toggle-label" style={{
                              opacity: usuarioEditando.rol !== 'superadmin' ? 0.5 : 1,
                              cursor: usuarioEditando.rol !== 'superadmin' ? 'not-allowed' : 'pointer'
                            }}>
                              <span className="toggle-button"></span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleGuardarPermisos}
            disabled={guardando}
          >
            {guardando ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Guardando...
              </>
            ) : (
              '💾 Guardar Permisos'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL PARA SUBIR FIRMA */}
      <Modal show={showModalFirma} onHide={() => setShowModalFirma(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            ✍️ Subir Firma - {usuarioFirma?.nombres}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Seleccionar imagen de la firma</label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleFileChange}
            />
            <small className="text-muted">Formatos: JPG, PNG, GIF (máx. 2MB)</small>
          </div>

          {firmaPreview && (
            <div className="firma-preview-container">
              <img 
                src={firmaPreview} 
                alt="Preview de firma" 
                className="firma-preview-image"
              />
            </div>
          )}

          {!firmaPreview && (
            <div className="firma-preview-container">
              <div className="text-muted">
                <div style={{fontSize: '48px'}}>✍️</div>
                <p>La vista previa aparecerá aquí</p>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalFirma(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubirFirma}
            disabled={!firmaPreview || subiendoFirma}
          >
            {subiendoFirma ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Guardando...
              </>
            ) : (
              '💾 Guardar Firma'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* MODAL PARA VER FIRMA COMPLETA */}
      <Modal show={showModalVerFirma} onHide={() => setShowModalVerFirma(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            👁️ Firma de {firmaViendo?.nombres}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {firmaViendo?.firma && (
            <img 
              src={firmaViendo.firma} 
              alt="Firma completa"
              style={{
                maxWidth: '100%',
                maxHeight: '400px',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '20px',
                background: 'white'
              }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModalVerFirma(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* TOAST NOTIFICATION */}
      {showToast && (
        <div className="toast-container">
          <div className={`toast-notification ${tipoMensaje}`}>
            <button className="toast-close" onClick={ocultarToast}>×</button>
            <div className="toast-header">
              <div className={`toast-icon ${tipoMensaje}`}>
                {tipoMensaje === 'success' && '✓'}
                {tipoMensaje === 'error' && '✕'}
                {tipoMensaje === 'loading' && '⟳'}
              </div>
              <span>
                {tipoMensaje === 'success' && 'Éxito'}
                {tipoMensaje === 'error' && 'Error'}
                {tipoMensaje === 'loading' && 'Procesando'}
              </span>
            </div>
            <div className="toast-message">{mensaje}</div>
          </div>
        </div>
      )}
    </>
  );
}

export default UserManagementPage;